import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UiStateService } from '@app/core/services/ui-state.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

export interface Plan {
  planCode: string;
  name: string;
  price: number;
  features: string[];
}

export interface Announcement {
  heading: string;
  message: string;
}

export type Language = 'english' | 'afrikaans';

@Injectable({
  providedIn: 'root',
})
export class NoraManagementService {
  limits: { [key: string]: number } = { Lite: 180, Premium: 1800, FreeTrial: 1800, Go: 1800 };

  plans: Plan[] = [
    {
      planCode: 'FreeTrial',
      name: 'Free Trial',
      price: 0,
      features: [
        '30-days unlimited free access',
        'Max of 30 mins per recording',
        'AI Dictation & Ambient AI Scribe',
        'Seamlessly integrated into Healthbridge Clinical',
        'Structured clinical notes',
        'Choose from a selection of note templates',
        'Email support',
      ],
    },
    {
      planCode: 'Lite',
      name: 'Lite',
      price: 0,
      features: [
        '300 recordings per month',
        'Max of 3 mins per recording',
        'AI Dictation mode',
        'Seamlessly integrated into Healthbridge Clinical',
        'Structured clinical notes',
        'Choose from a selection of note templates',
        'Dedicated customer support',
      ],
    },
    {
      planCode: 'Premium',
      name: 'Premium',
      price: 299,
      features: [
        '300 recordings per month',
        'Max of 30 mins per recording',
        'AI Dictation & Ambient AI Scribe',
        'Seamlessly integrated into Healthbridge Clinical',
        'Structured clinical notes',
        'Choose from a selection of note templates',
        'Dedicated customer support',
      ],
    },
    {
      planCode: 'Pro',
      name: 'Pro',
      price: 999,
      features: [
        'Unlimited recordings per month',
        'Unlimited mins per recording',
        'AI Dictation & Ambient AI Scribe',
        'Seamlessly integrated into Healthbridge Clinical',
        'Structured clinical notes',
        'Choose from a selection of note templates',
        'Dedicated customer support',
      ],
    },
  ];

  languages = [
    // South African languages
    { code: 'english', name: 'English' },
    { code: 'afrikaans', name: 'Afrikaans' },
  ];

  private showWhatIsNora: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showWhatIsNora$: Observable<boolean> = this.showWhatIsNora.asObservable();
  private showGetStarted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showGetStarted$: Observable<boolean> = this.showGetStarted.asObservable();
  public signUpComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public signUpComplete$: Observable<boolean> = this.signUpComplete.asObservable();
  private showSignUp: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showSignUp$: Observable<boolean> = this.showSignUp.asObservable();
  private subscriptionLevel: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public subscriptionLevel$: Observable<string> = this.subscriptionLevel.asObservable().pipe(
    distinctUntilChanged(),
    filter(level => !!level),
    tap(level =>
      level === 'none' ? this.uiStateService.setNoraSubscriptionLevel(null) : this.uiStateService.setNoraSubscriptionLevel(level)
    )
  );
  private subscriptionDaysLeft: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public subscriptionDaysLeft$: Observable<number> = this.subscriptionDaysLeft.asObservable();
  public isFreeTrialUsedUp: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isFreeTrialUsedUp$: Observable<boolean> = this.isFreeTrialUsedUp.asObservable();
  public monthlyUsageLimitReached: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public monthlyUsageLimitReached$: Observable<boolean> = this.monthlyUsageLimitReached.asObservable();
  public numberOfDaysLeftTillReset: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public numberOfDaysLeftTillReset$: Observable<number> = this.numberOfDaysLeftTillReset.asObservable();
  public announcements: BehaviorSubject<Announcement[]> = new BehaviorSubject<Announcement[]>([]);
  public announcements$: Observable<Announcement[]> = this.announcements.asObservable();
  public recordingMode: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public recordingMode$: Observable<string> = this.recordingMode.asObservable();

  constructor(private uiStateService: UiStateService) {}

  // getStarted
  setShowGetStarted(show: boolean) {
    this.showGetStarted.next(show);
  }

  getShowGetStarted(): Observable<boolean> {
    return this.showGetStarted$;
  }

  // signUp
  setSignUpComplete(complete: boolean) {
    this.signUpComplete.next(complete);
  }

  getSignUpComplete(): Observable<boolean> {
    return this.signUpComplete$;
  }

  // whatIsNora
  setShowWhatIsNora(show: boolean) {
    this.showWhatIsNora.next(show);
  }

  getShowWhatIsNora(): Observable<boolean> {
    return this.showWhatIsNora$;
  }

  // showSignUp
  setShowSignUp(show: boolean) {
    this.showSignUp.next(show);
  }

  getShowSignUp(): Observable<boolean> {
    return this.showSignUp$;
  }

  // subscriptionLevel
  setSubscriptionLevel(level: string) {
    this.subscriptionLevel.next(level);
  }

  getSubscriptionLevel(): Observable<string> {
    return this.subscriptionLevel$;
  }

  // subscriptionDaysLeft
  setSubscriptionDaysLeft(days: number) {
    this.subscriptionDaysLeft.next(days);
  }

  getSubscriptionDaysLeft(): Observable<number> {
    return this.subscriptionDaysLeft$;
  }

  setIsFreeTrialUsedUp(usedUp: boolean) {
    this.isFreeTrialUsedUp.next(usedUp);
  }

  getIsFreeTrialUsedUp(): Observable<boolean> {
    return this.isFreeTrialUsedUp$;
  }

  setMonthlyUsageLimitReached(reached: boolean) {
    this.monthlyUsageLimitReached.next(reached);
  }

  getMonthlyUsageLimitReached(): Observable<boolean> {
    return this.monthlyUsageLimitReached$;
  }

  setNumberOfDaysLeftTillReset(days: number) {
    this.numberOfDaysLeftTillReset.next(days);
  }

  getNumberOfDaysLeftTillReset(): Observable<number> {
    return this.numberOfDaysLeftTillReset$;
  }

  setRecordingMode(mode: string) {
    this.recordingMode.next(mode);
  }

  getRecordingMode(): Observable<string> {
    return this.recordingMode$;
  }

  setAnnouncements(announcements: Announcement[]) {
    this.announcements.next(announcements);
  }

  getAnnouncements(): Observable<Announcement[]> {
    return this.announcements$;
  }
}
